<template>
  <div class="v-date-range">
    <v-menu v-model="menus" :close-on-content-click="false" offset-y v-bind="menuProps">
      <template v-slot:activator="{ on }">
        <v-text-field
          class="v-date-range__input-field"
          :name="name"
          :label="label"
          :placeholder="placeholder"
          :value="inputValue"
          v-on="on"
          outlined
          dense
          hide-details
          :prepend-icon="icon"
        ></v-text-field>
      </template>
      <v-card class="v-date-range__menu-content">
        <v-card-text>
          <div
            :data-days="highlightDates.length"
            :class="{
              'v-date-range__pickers': true,
              'v-date-range--highlighted': highlightDates.length
            }"
          >
            <!-- <v-card-title v-if="$slots.title">
              <slot name="title" v-if="$slots.title"></slot>
            </v-card-title>-->
            <v-card-title v-if="!noPresets" class="p-0 mb-2">{{ presetLabel }}</v-card-title>
            <v-card-text class="p-0">
              <div class="v-date-range__content">
                <!-- <v-list v-if="!noPresets" class="mr-4">
                  <v-subheader class="p-0 title">{{ presetLabel }}</v-subheader>
                  <v-list-tile
                    v-for="(preset, index) in presets"
                    v-model="isPresetActive[index]"
                    :key="index"
                    @click="selectPreset(index)"
                  >
                    <v-list-tile-content>{{ preset.label }}</v-list-tile-content>
                  </v-list-tile>
                </v-list>-->
                <v-date-picker
                  class="mr-4 v-date-range__picker--start v-date-range__picker"
                  v-model="pickerStart"
                  :locale="locale"
                  :first-day-of-week="firstDayOfWeek"
                  :min="min"
                  :max="pickerEnd || max"
                  :no-title="noTitle"
                  :next-icon="nextIcon"
                  :prev-icon="prevIcon"
                  :events="highlightDates"
                  :event-color="highlightClasses"
                ></v-date-picker>
                <v-date-picker
                  class="v-date-range__picker--end v-date-range__picker"
                  v-model="pickerEnd"
                  :locale="locale"
                  :first-day-of-week="firstDayOfWeek"
                  :min="pickerStart || min"
                  :max="max"
                  :no-title="noTitle"
                  :next-icon="nextIcon"
                  :prev-icon="prevIcon"
                  :events="highlightDates"
                  :event-color="highlightClasses"
                ></v-date-picker>
              </div>
            </v-card-text>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="reset">{{ mergedActionLabels.reset }}</v-btn>
          <v-spacer></v-spacer>
          <v-btn text @click="menus = false">
            {{
            mergedActionLabels.cancel
            }}
          </v-btn>
          <v-btn
            @click="applyRange"
            color="primary"
            text
            :disabled="!bothSelected"
          >{{ mergedActionLabels.apply }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-menu>
  </div>
</template>
<script>
import { format, parse, differenceInCalendarDays, addDays } from "date-fns";
export default {
  name: "v-daterange",
  props: {
    // Take start and end as the input. Passable via v-model.
    value: {
      type: Object,
      default: () => {
        return {
          start: format(new Date(), "yyyy-MM-dd"),
          end: format(new Date(), "yyyy-MM-dd")
        };
      }
    },
    disabled: {
      type: Boolean,
      default: false
    },
    presets: {
      type: Array,
      default: () => {
        return [];
      }
    },
    noPresets: {
      type: Boolean,
      default: false
    },
    name: {
      type: String,
      default: "Range"
    },
    label: {
      type: String,
      default: "Range"
    },
    icon: {
      type: String,
      default: ""
    },

    // Denotes the Placeholder string for start date.
    startLabel: {
      type: String,
      default: "Start Date"
    },
    // Denotes the Placeholder string for start date.
    endLabel: {
      type: String,
      default: "End Date"
    },
    // The string that gets placed between `startLabel` and `endLabel`
    separatorLabel: {
      type: String,
      default: "-"
    },
    presetLabel: {
      type: String,
      default: "Presets"
    },
    actionLabels: {
      type: Object,
      default: () => {
        return {
          apply: "Apply",
          cancel: "Cancel",
          reset: "Reset"
        };
      }
    },
    /**
     * Following values are all passable to vuetify's own datepicker
     * component.
     */
    // Min selectable date.
    min: {
      type: String,
      default: undefined
    },
    // Max selectable date
    max: {
      type: String,
      default: undefined
    },
    // Locale
    locale: {
      type: String,
      default: "en-us"
    },
    firstDayOfWeek: {
      type: [String, Number],
      default: 0
    },
    noTitle: {
      type: Boolean,
      default: false
    },
    displayFormat: {
      type: String
    },
    highlightColor: {
      type: String,
      default: "blue lighten-5"
    },
    showReset: {
      type: Boolean,
      default: true
    },
    /**
     * Icons
     */
    nextIcon: {
      type: String,
      default: "$vuetify.icons.next"
    },
    prevIcon: {
      type: String,
      default: "$vuetify.icons.prev"
    },
    inputProps: {
      type: Object,
      default: () => {
        return {};
      }
    },
    menuProps: {
      type: Object,
      default: () => {
        return {};
      }
    }
  },
  data() {
    return {
      menus: false,
      pickerStart: this.value.start,
      pickerEnd: this.value.end,
      highlightDates: [],
      highlightClasses: {}
    };
  },
  computed: {
    inputValue() {
      if (this.isValueEmpty) {
        return "";
      }
      const start = this.displayFormat
        ? this.formatDate(this.value.start, this.displayFormat)
        : this.value.start;
      const end = this.displayFormat
        ? this.formatDate(this.value.end, this.displayFormat)
        : this.value.end;
      return `${start} ${this.separatorLabel} ${end}`;
    },
    placeholder() {
      return `${this.startLabel} ${this.separatorLabel} ${this.endLabel}`;
    },
    /**
     * If the value prop doesn't have any start value,
     * its most likely that an empty object was passed.
     */
    isValueEmpty() {
      return !this.value.start;
    },
    /**
     * If the user has selected both the dates or not
     */
    bothSelected() {
      return this.pickerStart && this.pickerEnd;
    },
    isPresetActive() {
      return this.presets.map(
        preset =>
          preset.range[0] === this.pickerStart &&
          preset.range[1] === this.pickerEnd
      );
    },
    mergedActionLabels() {
      return {
        ...{
          apply: "Terapkan",
          cancel: "Batal",
          reset: "Ulang"
        },
        ...this.actionLabels
      };
    }
  },
  methods: {
    /**
     * Emit the input event with the updated range data.
     * This makes v-model work.
     */
    applyRange() {
      this.menus = false;
      this.emitRange();
    },
    /**
     * Called every time the menu is closed.
     * It also emits an event to tell the parent
     * that the menu has closed.
     *
     * Upon closing the datepicker values are set
     * to the current selected value.
     */
    closeMenu() {
      // Reset the changed values for datepicker models.
      this.pickerStart = this.value.start;
      this.pickerEnd = this.value.end;
      this.highlight();
      this.$emit("menu-closed");
    },
    formatDate(date, fmt) {
      return format(parse(date, "yyyy-MM-dd", new Date()), fmt);
    },
    highlight() {
      if (!this.bothSelected) {
        return;
      }
      const dates = [];
      const classes = {};
      const start = parse(this.pickerStart, "yyyy-MM-dd", new Date());
      const end = parse(this.pickerEnd, "yyyy-MM-dd", new Date());
      const diff = Math.abs(differenceInCalendarDays(start, end));

      // Loop though all the days in range.
      for (let i = 0; i <= diff; i++) {
        const date = format(addDays(start, i), "yyyy-MM-dd");
        dates.push(date);
        const classesArr = [];
        classesArr.push(`v-date-range__in-range`);
        classesArr.push(this.highlightColor);
        i === 0 && classesArr.push(`v-date-range__range-start`);
        i === diff && classesArr.push(`v-date-range__range-end`);
        classes[date] = classesArr.join(" ");
      }
      this.highlightDates = dates;
      this.highlightClasses = classes;
    },
    selectPreset(presetIndex) {
      this.pickerStart = this.presets[presetIndex].range[0];
      this.pickerEnd = this.presets[presetIndex].range[1];
    },
    reset() {
      // Reset Picker Values
      this.pickerStart = "";
      this.pickerEnd = "";
      this.highlightDates = [];
      this.highlightClasses = {};
      this.emitRange();
    },
    emitRange() {
      this.$emit("input", {
        start: this.pickerStart,
        end: this.pickerEnd
      });
    }
  },
  watch: {
    // Watching to see if the menu is closed.
    menus(isOpen) {
      if (!isOpen) {
        this.closeMenu();
      } else {
        this.highlight();
      }
    },
    pickerStart: "highlight",
    pickerEnd: "highlight"
  }
};
</script>
<style scope>
.v-btn.accent .v-date-range__in-range {
  height: 0% !important;
  width: 0% !important;
}

.v-date-range__in-range {
  height: 100% !important;
  width: 100% !important;
  margin: 0 !important;
  border-radius: 0 !important;
}

.p-0 {
  padding: 0px !important;
}
</style>

